import HasWriteAccess from '@app/src/components/HasWriteAccess'
import CategoryDrawer from '@app/src/components/ManageProviderDrawer/CategoryDrawer/CategoryDrawer'
import { CategoryDrawerViews } from '@app/src/components/ManageProviderDrawer/CategoryDrawer/CategoryDrawerContent'
import TagsCell from '@app/src/components/Table/Cells/TagsCell'
import { useDialogState } from '@app/src/hooks/mui-hooks'
import type { Category } from '@app/src/types/categories'
import type { Provider } from '@app/src/types/organizations'
import { Add, CategoryOutlined } from '@mui/icons-material'
import { Avatar, Box, Button, Chip, Stack, Typography } from '@mui/material'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'

interface CustomCategoriesSectionProps {
  provider: Provider
  categories?: Category[]
}

const MAX_VISIBLE_TAGS = 4

const CustomCategoriesSection: React.FC<CustomCategoriesSectionProps> = ({ provider, categories }) => {
  const { formatMessage } = useIntl()
  const [isDrawerOpen, openDrawer, closeDrawer] = useDialogState(false)

  const hasCategories = Boolean(categories?.length)

  const tagsByCategoryId = useMemo(() => {
    const options = provider.categoryOptions
    if (!categories?.length || !options?.length) return new Map<number, string[]>()
    return new Map(categories.map(c => [c.id, options.filter(opt => opt.categoryId === c.id).map(opt => opt.name)]))
  }, [categories, provider.categoryOptions])

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="subtitle1" fontWeight={600}>
          {formatMessage({ id: 'schemas.supplier.customCategories' })}
        </Typography>
        <HasWriteAccess>
          <Button size="small" startIcon={<Add />} onClick={openDrawer}>
            {formatMessage({ id: 'general.createCategory' })}
          </Button>
        </HasWriteAccess>
      </Stack>

      {hasCategories ? (
        <Stack spacing={3} sx={{ bgcolor: 'common.white', borderRadius: 2, px: 3, py: 4 }}>
          <Stack spacing={3}>
            {categories?.map(category => {
              const tags = tagsByCategoryId.get(category.id) ?? []

              return (
                <Stack key={category.id} direction="row" alignItems="center" spacing={3}>
                  <Typography color="text.secondary" sx={{ width: '40%', flexShrink: 0 }}>
                    {category.name}
                  </Typography>
                  <Box sx={{ width: '60%', display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    {tags.length > 0 ? (
                      <TagsCell tags={tags} amountOfTagsShow={MAX_VISIBLE_TAGS} disableCell />
                    ) : (
                      <Chip
                        variant="outlined"
                        size="small"
                        disabled
                        label={formatMessage({ id: 'general.notAdded' })}
                      />
                    )}
                  </Box>
                </Stack>
              )
            })}
          </Stack>
        </Stack>
      ) : (
        <Stack
          alignItems="center"
          spacing={1.5}
          py={12}
          sx={{ bgcolor: 'grey.100', borderRadius: 2, border: 1, borderColor: 'divider' }}
        >
          <Avatar sx={{ bgcolor: 'grey.200', width: 48, height: 48 }}>
            <CategoryOutlined sx={{ fontSize: 24, color: 'text.disabled' }} />
          </Avatar>
          <Typography variant="subtitle1" fontWeight={600}>
            {formatMessage({ id: 'general.addCustomCategories' })}
          </Typography>
          <Typography color="text.secondary" textAlign="center" maxWidth={300}>
            {formatMessage({ id: 'general.addCustomCategoriesDescription' })}
          </Typography>
          <HasWriteAccess>
            <Button variant="contained" size="small" startIcon={<Add />} onClick={openDrawer} sx={{ mt: 1 }}>
              {formatMessage({ id: 'general.createCategory' })}
            </Button>
          </HasWriteAccess>
        </Stack>
      )}

      <CategoryDrawer
        categories={categories}
        view={CategoryDrawerViews.CreateCategory}
        isDrawerOpen={isDrawerOpen}
        closeDrawer={closeDrawer}
      />
    </Box>
  )
}

export default CustomCategoriesSection
